import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import "../hooks";
import "../api";

import { KnowledgeBaseManager } from "../components/KnowledgeBaseManager";
import { useChatDataProvider } from "../providers/useChatDataProvider";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Separator } from "@/components/ui/v2/separator";
import { ROUTES } from "@/constants/routes";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import { useIntegrationModuleDetailBreadcrumbs } from "@/features/integrations/modules/hooks/useIntegrationModuleDetailBreadcrumbs";
import {
  getActionHandlers,
  renderComponent,
} from "@/features/integrations/shared/renderer";
import { useSchemaIntegrationData } from "@/features/integrations/shared/renderer/hooks/useSchemaIntegrationData";
import { decodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";

function ChatTenantDetailPage() {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const { integrationName: rawIntegrationName, moduleName: moduleNameParam } =
    useParams<{ integrationName?: string; moduleName?: string }>();

  const decodedIntegrationName = rawIntegrationName
    ? decodeSlug(rawIntegrationName)
    : null;
  const backendIntegrationName = decodedIntegrationName ?? "";
  const effectiveModuleName = moduleNameParam
    ? decodeSlug(moduleNameParam)
    : "chat";

  const { breadcrumbs } = useIntegrationModuleDetailBreadcrumbs("chat");

  const moduleQuery = useIntegrationModule({
    integrationName: backendIntegrationName,
    moduleName: effectiveModuleName,
    tenantId: tenantId ?? undefined,
  });

  const isModuleActive = moduleQuery.data?.isActive ?? false;

  const provider = useChatDataProvider(decodedIntegrationName ?? undefined);
  const ui = useMemo(() => provider.getClientViews(), [provider]);

  const integrationData = useSchemaIntegrationData(
    backendIntegrationName,
    "chat",
    undefined,
    tenantId ?? undefined,
  );

  const actionHandlers = useMemo(
    () => getActionHandlers(backendIntegrationName),
    [backendIntegrationName],
  );

  const executeAction = useCallback(
    async (actionKey: string) => {
      const handler = actionHandlers[actionKey];
      if (handler) {
        await handler({
          integrationName: backendIntegrationName,
          moduleName: effectiveModuleName,
          tenantId: tenantId ?? undefined,
        });
      }
    },
    [actionHandlers, backendIntegrationName, effectiveModuleName, tenantId],
  );

  const context = useMemo(
    () => ({
      integrationName: backendIntegrationName,
      moduleName: effectiveModuleName,
      tenantId: tenantId ?? undefined,
      executeAction,
      data: integrationData.data,
      isLoading: integrationData.isLoading,
      emptyMessage: t("common.noResultsFound"),
    }),
    [
      backendIntegrationName,
      effectiveModuleName,
      tenantId,
      executeAction,
      integrationData.data,
      integrationData.isLoading,
      t,
    ],
  );

  if (moduleQuery.isPending) return null;
  if (moduleQuery.isError || !moduleQuery.data || !isModuleActive) return null;

  const backHref = rawIntegrationName
    ? `/${ROUTES.integrations}/${rawIntegrationName}`
    : `/${ROUTES.integrations}`;

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      title={t("integrations.modules.names.chat", { defaultValue: "Chat" })}
      subtitle={t("integrations.modules.descriptions.chat", {
        defaultValue: "Captain AI assistant configuration and knowledge base",
      })}
      backbutton={{ label: t("common.back"), href: backHref }}
    >
      {ui.length > 0 && (
        <div className="space-y-6">
          {ui.map((component, index) => (
            <div key={`${component.component}-${index}`}>
              {renderComponent(component, context)}
            </div>
          ))}
        </div>
      )}

      {ui.length > 0 && <Separator className="my-6" />}

      <KnowledgeBaseManager integrationName={rawIntegrationName ?? "captain"} />
    </ModernPageLayout>
  );
}

export default ChatTenantDetailPage;
